import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import ProgressBar from '../components/ProgressBar';
import { stayOptions } from '../data/mockData';

const StayPage: React.FC = () => {
  const navigate = useNavigate();
  const { setStay, state } = useBooking();
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // 篩選狀態
  const [filters, setFilters] = useState({
    view: 'all', // 海景、山景、市區、鄉村
    features: 'all', // WiFi、工作桌、廚房等
    priceRange: 'all', // 價格範圍
    digitalNomad: false, // 數位游牧友善
    sharedSpace: false, // 有共用空間
    monthlyRent: false, // 可月租
  });

  const handleFilterChange = (filterType: string, value: any) => {
    setFilters({ ...filters, [filterType]: value });
    setCurrentPage(1); // 重置到第一頁
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // 重置到第一頁
  };

  const handleOptionSelect = (option: any) => {
    setSelectedOption(option);
  };

  const handleNext = () => {
    if (selectedOption) {
      setStay(selectedOption);
      navigate('/workspace-booking');
    }
  };

  const handleSkip = () => {
    navigate('/workspace-booking');
  };

  // 根據篩選條件和搜尋推薦住宿
  const filteredAndRecommendedOptions = useMemo(() => {
    let filtered = stayOptions;

    // 搜尋篩選
    if (searchTerm.trim()) {
      filtered = filtered.filter(option => 
        option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // 根據景觀篩選
    if (filters.view !== 'all') {
      filtered = filtered.filter(option => option.type === filters.view);
    }

    // 根據特色篩選
    if (filters.features !== 'all') {
      filtered = filtered.filter(option => option.tags.includes(filters.features));
    }

    // 根據價格範圍篩選
    if (filters.priceRange !== 'all') {
      filtered = filtered.filter(option => {
        const price = parseInt(option.price.replace(/[^0-9]/g, ''));
        switch (filters.priceRange) {
          case 'budget': return price <= 1000;
          case 'mid': return price > 1000 && price <= 2000;
          case 'premium': return price > 2000;
          default: return true;
        }
      });
    }

    // 根據數位游牧需求篩選
    if (filters.digitalNomad) {
      filtered = filtered.filter(option => 
        option.features.includes('數位游牧友善')
      );
    }

    if (filters.sharedSpace) {
      filtered = filtered.filter(option => 
        option.features.includes('共用空間')
      );
    }

    if (filters.monthlyRent) {
      filtered = filtered.filter(option => 
        option.features.includes('可月租')
      );
    }

    // 計算推薦分數
    const scoredOptions = filtered.map(option => {
      let score = 0;
      
      // 根據用戶類型加分
      if (state.userType === 'foreign' && option.features.includes('數位游牧友善')) {
        score += 3;
      }
      
      // 根據特色加分
      if (option.tags.includes('Wi-Fi')) score += 2;
      if (option.tags.includes('工作桌')) score += 2;
      if (option.tags.includes('廚房')) score += 1;
      
      // 根據價格加分（性價比）
      const price = parseInt(option.price.replace(/[^0-9]/g, ''));
      if (price <= 1200) score += 2;
      
      return { ...option, score };
    });

    // 按推薦分數排序
    return scoredOptions.sort((a, b) => b.score - a.score);
  }, [filters, state.userType, searchTerm]);

  // 分頁計算
  const totalPages = Math.ceil(filteredAndRecommendedOptions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOptions = filteredAndRecommendedOptions.slice(startIndex, endIndex);

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== 'all' && value !== false
  ) || searchTerm.trim() !== '';

  // 分頁控制函數
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <ProgressBar currentStep={3} />
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            選擇您的住宿
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            根據您的需求篩選，我們會為您推薦最適合的住宿選項
          </p>
        </div>

        {/* 篩選器區域 */}
        <div className="bg-white rounded-2xl shadow-soft p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">篩選條件</h2>
          
          {/* 搜尋框 */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🔍 搜尋住宿
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="搜尋住宿名稱、特色、景觀類型..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    // 搜尋功能已在 useMemo 中實現，這裡可以加入額外的搜尋邏輯
                  }
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              />
              <button
                onClick={() => {
                  // 搜尋功能已在 useMemo 中實現，這裡可以加入額外的搜尋邏輯
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-medium hover:shadow-strong"
              >
                🔍 搜尋
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 景觀類型 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🌄 景觀類型
              </label>
              <select
                value={filters.view}
                onChange={(e) => handleFilterChange('view', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">全部景觀</option>
                <option value="海景">海景</option>
                <option value="山景">山景</option>
                <option value="市區">市區</option>
                <option value="鄉村">鄉村</option>
              </select>
            </div>

            {/* 設施特色 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🏠 設施特色
              </label>
              <select
                value={filters.features}
                onChange={(e) => handleFilterChange('features', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">全部設施</option>
                <option value="Wi-Fi">Wi-Fi</option>
                <option value="工作桌">工作桌</option>
                <option value="廚房">廚房</option>
                <option value="停車場">停車場</option>
                <option value="觀景台">觀景台</option>
                <option value="健身房">健身房</option>
                <option value="游泳池">游泳池</option>
                <option value="SPA">SPA</option>
                <option value="餐廳">餐廳</option>
                <option value="商務中心">商務中心</option>
                <option value="露台">露台</option>
                <option value="農場體驗">農場體驗</option>
                <option value="茶園體驗">茶園體驗</option>
                <option value="森林浴">森林浴</option>
                <option value="健行步道">健行步道</option>
                <option value="溫泉">溫泉</option>
                <option value="露營車">露營車</option>
                <option value="樹屋">樹屋</option>
                <option value="玻璃屋">玻璃屋</option>
                <option value="私人泳池">私人泳池</option>
                <option value="管家服務">管家服務</option>
                <option value="私人花園">私人花園</option>
                <option value="私人海灘">私人海灘</option>
                <option value="文創氛圍">文創氛圍</option>
                <option value="便利商店">便利商店</option>
                <option value="圖書館">圖書館</option>
                <option value="有機食材">有機食材</option>
                <option value="戶外廚房">戶外廚房</option>
                <option value="海灘">海灘</option>
                <option value="森林景觀">森林景觀</option>
                <option value="全景景觀">全景景觀</option>
              </select>
            </div>

            {/* 價格範圍 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💰 價格範圍
              </label>
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">全部價格</option>
                <option value="budget">經濟型 (NT$ 1000以下)</option>
                <option value="mid">中價位 (NT$ 1000-2000)</option>
                <option value="premium">高級型 (NT$ 2000以上)</option>
              </select>
            </div>

            {/* 數位游牧需求 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💻 數位游牧需求
              </label>
              <select
                value={filters.digitalNomad ? 'digitalNomad' : filters.sharedSpace ? 'sharedSpace' : filters.monthlyRent ? 'monthlyRent' : 'all'}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({
                    ...filters,
                    digitalNomad: value === 'digitalNomad',
                    sharedSpace: value === 'sharedSpace',
                    monthlyRent: value === 'monthlyRent'
                  });
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
              >
                <option value="all">全部需求</option>
                <option value="digitalNomad">數位游牧友善</option>
                <option value="sharedSpace">有共用空間</option>
                <option value="monthlyRent">可月租</option>
              </select>
            </div>
          </div>

          {/* 清除篩選按鈕 */}
          {hasActiveFilters && (
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setFilters({
                    view: 'all',
                    features: 'all',
                    priceRange: 'all',
                    digitalNomad: false,
                    sharedSpace: false,
                    monthlyRent: false,
                  });
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors duration-300 font-medium"
              >
                🗑️ 清除所有篩選
              </button>
            </div>
          )}
        </div>

        {/* 推薦結果 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              推薦住宿 ({filteredAndRecommendedOptions.length} 個選項)
            </h2>
            {hasActiveFilters && (
              <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                🔍 已套用篩選條件
              </span>
            )}
          </div>
        </div>

        {/* 住宿選項 */}
        {filteredAndRecommendedOptions.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {currentOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionSelect(option)}
                  className={`bg-white rounded-2xl shadow-soft overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-medium relative ${
                    selectedOption?.id === option.id
                      ? 'ring-4 ring-blue-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {/* 推薦標籤 */}
                  {startIndex + index < 2 && (
                    <div className="absolute top-4 left-4 z-10">
                      <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-medium">
                        {startIndex + index === 0 ? '🥇 最推薦' : '🥈 推薦'}
                      </span>
                    </div>
                  )}

                  <div className="relative">
                    <img
                      src={option.image}
                      alt={option.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-semibold shadow-soft">
                        {option.price}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {option.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{option.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {option.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {option.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {selectedOption?.id === option.id && (
                      <div className="mt-4 text-center">
                        <div className="inline-flex items-center text-blue-600 font-semibold">
                          <span className="mr-2">✓</span>
                          已選擇
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 分頁控制 */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mb-8">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-xl transition-colors duration-300 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  ← 上一頁
                </button>

                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-2 rounded-lg transition-colors duration-300 ${
                        currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-xl transition-colors duration-300 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  下一頁 →
                </button>
              </div>
            )}

            {/* 分頁資訊 */}
            <div className="text-center text-gray-600 mb-6">
              顯示第 {startIndex + 1} - {Math.min(endIndex, filteredAndRecommendedOptions.length)} 項，
              共 {filteredAndRecommendedOptions.length} 項結果
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft p-12 text-center mb-8">
            <div className="text-6xl mb-4">🏠</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              沒有找到符合條件的住宿
            </h3>
            <p className="text-gray-600 mb-4">
              請嘗試調整篩選條件或搜尋關鍵字
            </p>
            <button
              onClick={() => {
                setFilters({
                  view: 'all',
                  features: 'all',
                  priceRange: 'all',
                  digitalNomad: false,
                  sharedSpace: false,
                  monthlyRent: false,
                });
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors duration-300"
            >
              查看所有選項
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {selectedOption && (
            <button
              onClick={handleNext}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
            >
              下一步
            </button>
          )}
          
          <button
            onClick={handleSkip}
            className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-2xl transition-all duration-300"
          >
            我已安排住宿
          </button>
        </div>

        {selectedOption && (
          <div className="mt-8 bg-blue-50 rounded-2xl p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              已選擇：{selectedOption.name}
            </h3>
            <p className="text-blue-600">
              價格：{selectedOption.price} | 類型：{selectedOption.type}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StayPage; 